/**
 * «Pubblica anche in…»: gli altri obblighi in cui mettere lo stesso file.
 *
 * Il Comune caricava tre volte lo stesso regolamento in tre obblighi. Qui lo si
 * carica una volta e si spuntano gli altri: ciascuno riceve una copia **sua** —
 * con il nome ANAC, l'indirizzo e la durata di quell'obbligo — e le copie
 * restano legate, per dire «pubblicato anche in…» e per sostituirle insieme.
 *
 * Le voci sono etichettate con il **percorso** e non con la sola
 * denominazione: fra i 121 obblighi cinquantadue denominazioni si ripetono, e
 * una casella «Atti generali» non direbbe in quale sezione finisce il file.
 * Gli obblighi scelti salgono in cima, con la propria durata: ogni obbligo ha
 * la sua regola, e la copia la segue salvo scelta diversa.
 *
 * E' un `fieldset` con una `legend`: lo screen reader annuncia il gruppo prima
 * delle caselle, e la ricerca e' un campo vero, non un filtro a tasti.
 */

import { useId, useMemo, useState } from "react";
import type { IdNodo, Nodo } from "../api/types";
import { filtraObblighi } from "../lib/copie";
import type { SceltaCopia } from "../lib/copie";
import { DURATA_MASSIMA_AL_CARICAMENTO } from "../lib/scadenze";
import { CampoDurata } from "./CampoDurata";

export function SceltaObblighi({
  candidati,
  scelte,
  onCambia,
  percorsoDi,
  durataObbligoDi,
  regoleNote = true,
}: {
  /** Gli obblighi che possono ricevere una copia (`obblighiCandidati`). */
  candidati: readonly Nodo[];
  scelte: readonly SceltaCopia[];
  onCambia: (scelte: SceltaCopia[]) => void;
  /** Percorso leggibile di un obbligo, per l'etichetta e per la ricerca. */
  percorsoDi: (idNodo: IdNodo) => string;
  /** Durata predefinita dell'obbligo, se il Comune ne ha dichiarata una. */
  durataObbligoDi: (idNodo: IdNodo) => number | null | undefined;
  /** Falso se le regole degli obblighi non sono (ancora) disponibili. */
  regoleNote?: boolean;
}) {
  const idRicerca = useId();
  const [testo, setTesto] = useState("");
  const scelti = new Set(scelte.map((voce) => voce.id_nodo));
  const visibili = useMemo(
    () =>
      filtraObblighi(candidati, testo, percorsoDi).filter(
        (nodo) => !scelte.some((voce) => voce.id_nodo === nodo.id_nodo),
      ),
    [candidati, testo, percorsoDi, scelte],
  );

  const commuta = (idNodo: IdNodo) =>
    onCambia(
      scelti.has(idNodo)
        ? scelte.filter((voce) => voce.id_nodo !== idNodo)
        : [...scelte, { id_nodo: idNodo, scelta: "", finoAl: "" }],
    );

  const aggiorna = (idNodo: IdNodo, modifica: Partial<SceltaCopia>) =>
    onCambia(scelte.map((voce) => (voce.id_nodo === idNodo ? { ...voce, ...modifica } : voce)));

  return (
    <fieldset className="scelta-obblighi">
      <legend className="campo__etichetta">Pubblica anche in…</legend>
      <p className="campo__aiuto">
        Facoltativo. Lo stesso file diventa un documento di ciascun obbligo scelto, con il suo nome,
        il suo indirizzo e la sua durata. Si carica una volta sola; le cartelle valgono solo per
        l'obbligo principale.
      </p>

      {scelte.length > 0 ? (
        <ul className="pila-stretta scelta-obblighi__scelti">
          {scelte.map((voce) => (
            <li key={voce.id_nodo} className="scelta-obblighi__scelto">
              <label className="casella">
                <input type="checkbox" checked onChange={() => commuta(voce.id_nodo)} />
                <span>{percorsoDi(voce.id_nodo)}</span>
              </label>
              <CampoDurata
                etichetta="Durata in questo obbligo"
                massimoAnni={DURATA_MASSIMA_AL_CARICAMENTO}
                scelta={voce.scelta}
                finoAl={voce.finoAl}
                durataObbligo={durataObbligoDi(voce.id_nodo)}
                regoleNote={regoleNote}
                onCambia={(scelta, finoAl) => aggiorna(voce.id_nodo, { scelta, finoAl })}
              />
            </li>
          ))}
        </ul>
      ) : null}

      <label className="campo" htmlFor={idRicerca}>
        <span className="campo__etichetta">Cerca un obbligo</span>
        <input
          id={idRicerca}
          className="campo__controllo"
          type="search"
          value={testo}
          onChange={(evento) => setTesto(evento.target.value)}
        />
      </label>
      <div className="elenco-caselle">
        {visibili.length === 0 ? (
          <p className="campo__aiuto" style={{ padding: "0.5rem" }}>
            Nessun altro obbligo corrisponde alla ricerca.
          </p>
        ) : (
          visibili.map((nodo) => (
            <label key={nodo.id_nodo} className="casella">
              <input type="checkbox" checked={false} onChange={() => commuta(nodo.id_nodo)} />
              <span>{percorsoDi(nodo.id_nodo)}</span>
            </label>
          ))
        )}
      </div>
    </fieldset>
  );
}
